import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Box, Typography, CircularProgress, Alert, Paper, Button } from '@mui/material';
import { API_CONFIG } from '../../helpers/constants';
import { getFreshNodeApiHeaders, refreshAccessToken } from '../../services/authService';

interface TokenResponse {
  access_token: string;
  refresh_token: string;
  expires_in: number;
  token_type: string;
  scope: string;
  email_id?: string;
  tenant_id?: string;
  clientSecret?: string;
}

const SharePointOAuthCallback: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tokenData, setTokenData] = useState<TokenResponse | null>(null);
  // Single notify on cancel (avoids duplicate postMessage when Strict Mode double-invokes the effect)
  const cancelledNotifySentRef = useRef(false);

  useEffect(() => {
    const handleOAuthCallback = async () => {
      try {
        const code = searchParams.get('code');
        const state = searchParams.get('state');
        const error = searchParams.get('error');
        const errorDescription = searchParams.get('error_description');

        if (error) {
          // Popup flow: tell opener and close — same pattern as Gmail OAuth callback
          if (window.opener) {
            if (cancelledNotifySentRef.current) return;
            cancelledNotifySentRef.current = true;
            window.opener.postMessage(
              {
                type: 'OAUTH_CANCELLED',
                provider: 'sharepoint',
                error,
                errorDescription: errorDescription || undefined,
              },
              window.location.origin
            );
            window.close();
            return;
          }
          setError(`OAuth error: ${error}${errorDescription ? ` - ${errorDescription}` : ''}`);
          setLoading(false);
          return;
        }

        if (!code) {
          setError('No authorization code received');
          setLoading(false);
          return;
        }

        // Construct redirectUri based on current origin
        const redirectUri = `${window.location.origin}/auth/sharepoint/callback`;

        // Call the server endpoint to exchange code for tokens
        // Using the same Office 365 backend endpoint but with different redirect URI
        const tokenRequest = () => fetch(`${API_CONFIG.NODE_BASE_URL}/rest/office365/generateToken`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...nodeApiHeaders,
          },
          body: JSON.stringify({ code, redirectUri }),
        });

        let nodeApiHeaders = await getFreshNodeApiHeaders();
        if (!nodeApiHeaders.Authorization) {
          throw new Error('Your session has expired. Please sign in again and reconnect SharePoint.');
        }

        let response = await tokenRequest();
        if (response.status === 401) {
          const newAccessToken = await refreshAccessToken();
          if (!newAccessToken) {
            throw new Error('Your session has expired. Please sign in again and reconnect SharePoint.');
          }
          nodeApiHeaders = { Authorization: `Bearer ${newAccessToken}` };
          response = await tokenRequest();
        }

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const tokenResponse: TokenResponse = await response.json();
        setTokenData(tokenResponse);
        setLoading(false);

        // Send data back to parent window and close
        if (window.opener) {
          window.opener.postMessage({
            type: 'OAUTH_SUCCESS',
            data: {
              app_id: state,
              provider: 'sharepoint',
              ...tokenResponse,
              email_id: tokenResponse.email_id || 'user@example.com'
            }
          }, window.location.origin);
          window.close();
        }
      } catch (err) {
        console.error('Error processing OAuth callback:', err);
        setError(err instanceof Error ? err.message : 'An error occurred');
        setLoading(false);
      }
    };

    handleOAuthCallback();
  }, [searchParams]);

  const handleGoBack = () => {
    if (window.opener) {
      window.close();
    } else {
      navigate('/supportSpace');
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          gap: 2,
        }}
      >
        <CircularProgress size={60} />
        <Typography variant="h6" color="text.secondary">
          Connecting to SharePoint...
        </Typography>
      </Box>
    );
  }

  // If we have a parent window, don't show the success screen - just close
  if (window.opener && !error) {
    return null;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        p: 3,
      }}
    >
      <Paper
        elevation={3}
        sx={{
          p: 4,
          maxWidth: 600,
          width: '100%',
          textAlign: 'center',
        }}
      >
        {error ? (
          <>
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
            <Button variant="contained" onClick={handleGoBack}>
              Go Back
            </Button>
          </>
        ) : (
          <>
            <Typography variant="h4" gutterBottom color="success.main">
              ✅ SharePoint Connected!
            </Typography>
            
            <Typography variant="body1" sx={{ mb: 3 }}>
              Your SharePoint has been successfully connected.
            </Typography>

            <Button variant="contained" onClick={handleGoBack}>
              Continue
            </Button>
          </>
        )}
      </Paper>
    </Box>
  );
};

export default SharePointOAuthCallback;
